import useSWR from "swr";
import {
  fetchViewImage,
  ViewImageSize,
} from "lib/actions/views/fetchViewImage";
import { useViewsSnapshotUploads } from "lib/contexts/GlobalContexts/ViewsSnapshotUploadContext";
import { useObjectUrl } from "lib/hooks/useObjectUrl";

/**
 * Returns an object URL for a view's image, owned by the calling component.
 *
 * Newly-captured views have their screenshot held in the local upload cache
 * before the server has processed them. Reading that image synchronously from
 * React state avoids a stale-closure race that would otherwise cause SWR to
 * fire a server request before the upload is complete, resulting in a 404.
 * The local image is full size, and is used for thumbnails too.
 *
 * Server-side images (views loaded from the API) are fetched via SWR as normal.
 */
export function useViewImage(
  view:
    | {
        projectId?: number | null;
        snapshotId?: string | null;
        annotatedSnapshotId?: string | null;
      }
    | undefined,
  size: ViewImageSize = "full",
) {
  const { getSnapshotUploadLocal, getAnnotatedSnapshotUploadLocal } =
    useViewsSnapshotUploads();

  let localImage: Blob | null = null;
  if (view?.snapshotId) {
    const snapshotUpload = view.annotatedSnapshotId
      ? getAnnotatedSnapshotUploadLocal(
          view.snapshotId,
          view.annotatedSnapshotId,
        )
      : getSnapshotUploadLocal(view.snapshotId);
    localImage = snapshotUpload?.image ?? null;
  }

  // Only fetch from the server when the image isn't already in local cache.
  const serverFetchKey =
    !localImage && view?.snapshotId && view?.projectId
      ? `view-image-${size}-${view.projectId}-${view.snapshotId}-${view.annotatedSnapshotId ?? ""}`
      : null;

  const { data: serverImage, isLoading } = useSWR(
    serverFetchKey,
    () =>
      fetchViewImage(
        view!.projectId!,
        view!.snapshotId!,
        view!.annotatedSnapshotId ?? undefined,
        size,
      ),
    { revalidateOnFocus: false },
  );

  return {
    viewImageUrl: useObjectUrl(localImage ?? serverImage ?? null),
    isLoadingViewImage: !localImage && isLoading,
  };
}
